import { Injectable } from '@angular/core';

import hash from 'object-hash';
import { SettingConfiguration } from './setting-configuration.model';
import { ConfigurationService } from '../configuration/configuration.service';

@Injectable()
export class SettingService {
  private configuration: SettingConfiguration;
  private cache: {
    [key: string]: any
  } = {};

  constructor(private configurationService: ConfigurationService) { }

  private init() {
    this.configuration = this.configurationService.getSettingConfiguration();
  }

  private getKey = (key: string | object) => {
    if (!key) {
      throw new Error(`[Setting Service] key is required`);
    }
    const hashedKey = typeof (key) === 'string' ? key : hash(key);
    if (this.configuration?.convertKey) {
      return this.configuration.convertKey(hashedKey);
    }
    return hashedKey;
  }

  async hasKey(key: string | object): Promise<boolean> {
    this.init();
    try {
      const hashKey = this.getKey(key);
      if (this.cache[hashKey] === undefined) {
        let result = localStorage.getItem(hashKey);
        if (this.configuration?.get) {
          result = await this.configuration.get(hashKey);
        }
        if (!result) {
          this.cache[hashKey] = null;
        } else {
          this.cache[hashKey] = JSON.parse(result);
        }
      }
      return !!this.cache[hashKey];
    } catch (err) {
      console.error(`[SETTING HASKEY] ${err}`);
      return null;
    }
  }

  async set<T = any>(key: string | object, value: T, args?: any) {
    this.init();
    const hashKey = this.getKey(key);
    if (hashKey) {
      localStorage.setItem(hashKey, JSON.stringify(value));
      if (this.configuration?.set) {
        await this.configuration.set(hashKey, value, args);
      }
      this.cache[hashKey] = value;
    }
  }

  async get<T = any>(key: string | object, args?: any): Promise<T> {
    this.init();
    try {
      const hashKey = this.getKey(key);
      if (this.cache[hashKey] === undefined) {
        let result = localStorage.getItem(hashKey);
        if (this.configuration?.get) {
          result = await this.configuration.get(hashKey, args);
        }
        if (!result) {
          this.cache[hashKey] = null;
        } else {
          this.cache[hashKey] = JSON.parse(result);
        }
      }
      return this.cache[hashKey];
    } catch (err) {
      console.error(`[SETTING GET] ${err}`);
      return null;
    }
  }

  async remove(key: string | object, args?: any) {
    this.init();
    const hashKey = typeof (key) === 'string' ? key : hash(key);
    if (hashKey) {
      localStorage.removeItem(hashKey);
      if (this.configuration?.remove) {
        await this.configuration.remove(hashKey, args);
      }
      this.cache[hashKey] = null;
    }
  }
}
